import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import InputField from "./InputField";
import SelectField from "./SelectField";
import { toast } from "react-toastify";

const API_URL = process.env.REACT_APP_API_URL;

// Compara dos arreglos de IDs sin importar el orden.
const mismosIds = (a, b) =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

const UserForm = ({ user, onClose, onSave }) => {
  /* =========================
     ESTADO PRINCIPAL
  ========================= */
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
  });

  const [roles, setRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  /* =========================
     CLIENTES / BODEGAS
  ========================= */
  const [clientes, setClientes] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);

  const [clientesAsignados, setClientesAsignados] = useState([]);
  const [almacenesAsignados, setAlmacenesAsignados] = useState([]);

  /* =========================
     SEDES / GRUPOS DE SEDES
  ========================= */
  const [sedesDisponibles, setSedesDisponibles] = useState([]);
  const [gruposDisponibles, setGruposDisponibles] = useState([]);
  const [sedesYGruposAsignados, setSedesYGruposAsignados] = useState([]);
  const [accesoTotalSedes, setAccesoTotalSedes] = useState(false);

  // "Ready" gates: evita que el efecto de poda corra antes de que AMBOS
  // catálogos (sedes y grupos) hayan terminado de cargar para los
  // clientesAsignados vigentes. Ver commit fix(scope) para el detalle.
  // sedesParaClientes guarda el arreglo exacto de clientesAsignados para el
  // que se cargó sedesDisponibles (no un booleano) para que la poda pueda
  // compararlo contra el clientesAsignados vigente y detectar datos obsoletos
  // sin importar en qué commit de React haya llegado cada actualización.
  const [sedesParaClientes, setSedesParaClientes] = useState([]);
  const [gruposListos, setGruposListos] = useState(false);

  const token = localStorage.getItem("token");

  /* =========================
     OBTENER ROLES
  ========================= */
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/roles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRoles(data || []);
      } catch (error) {
        toast.error("Error al obtener roles:", error);
      }
    };

    fetchRoles();
  }, [token]);

  /* =========================
     CREAR / EDITAR (FORM DATA)
  ========================= */
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        role: user.role?._id || "",
      });
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
      });
      setClientesAsignados([]);
      setAlmacenesAsignados([]);
      setSedesYGruposAsignados([]);
      setAccesoTotalSedes(false);
    }
  }, [user]);

  /* =========================
     ¿EL ROL SELECCIONADO ES ADMIN?
     (SEGURO, SIN RACE CONDITION)
  ========================= */
  const esAdminDelUsuarioEditado = useMemo(() => {
    if (!roles.length || !formData.role) return null; // ⛔ esperar roles
    const rol = roles.find((r) => r._id === formData.role);
    return rol?.permissions?.includes("*") || false;
  }, [roles, formData.role]);

  const gruposVisibles = useMemo(() => {
    const sedeIds = new Set(sedesDisponibles.map((s) => s._id));
    return gruposDisponibles.filter((g) =>
      (g.sedes || []).some((s) => sedeIds.has(s._id)),
    );
  }, [sedesDisponibles, gruposDisponibles]);

  const opcionesSedesYGrupos = useMemo(
    () => [
      ...sedesDisponibles.map((s) => ({ ...s, __tipo: "sede" })),
      ...gruposVisibles.map((g) => ({ ...g, __tipo: "grupo" })),
    ],
    [sedesDisponibles, gruposVisibles],
  );

  /* =========================
     CARGAR CLIENTES / BODEGAS
     SOLO SI NO ES ADMIN
  ========================= */
  useEffect(() => {
    if (esAdminDelUsuarioEditado === null) return;
    if (esAdminDelUsuarioEditado === true) return;

    const fetchData = async () => {
      try {
        const [cRes, aRes] = await Promise.all([
          axios.get(`${API_URL}/clientes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API_URL}/almacenes`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setClientes(cRes.data || []);
        setAlmacenes(aRes.data || []);
      } catch (error) {
        toast.error("Error cargando clientes/bodegas:", error);
      }
    };

    fetchData();
  }, [token, esAdminDelUsuarioEditado]);

  /* =========================
     CARGAR GRUPOS DE SEDES (EMPRESA)
     SOLO SI NO ES ADMIN
  ========================= */
  useEffect(() => {
    if (esAdminDelUsuarioEditado === null) return;
    if (esAdminDelUsuarioEditado === true) return;

    const fetchGrupos = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/sede-grupos`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setGruposDisponibles(data || []);
      } catch (error) {
        toast.error("Error cargando grupos de sedes:", error);
      } finally {
        setGruposListos(true);
      }
    };

    fetchGrupos();
  }, [token, esAdminDelUsuarioEditado]);

  /* =========================
     CASCADA: SEDES DISPONIBLES SEGÚN CLIENTES ELEGIDOS
  ========================= */
  useEffect(() => {
    const clientesParaEstaCarga = clientesAsignados;

    if (!clientesParaEstaCarga.length) {
      setSedesDisponibles([]);
      setSedesParaClientes([]);
      return;
    }

    let cancelled = false;

    const fetchSedes = async () => {
      try {
        const respuestas = await Promise.all(
          clientesParaEstaCarga.map((clienteId) =>
            axios.get(`${API_URL}/sedes/${clienteId}`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ),
        );
        if (!cancelled) {
          setSedesDisponibles(respuestas.flatMap((r) => r.data || []));
          setSedesParaClientes(clientesParaEstaCarga);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error("Error cargando sedes:", error);
        }
      }
    };

    fetchSedes();

    return () => {
      cancelled = true;
    };
  }, [clientesAsignados, token]);

  /* =========================
     LIMPIAR SEDES/GRUPOS QUE YA NO PERTENECEN
     A LOS CLIENTES VIGENTES
  ========================= */
  useEffect(() => {
    if (!gruposListos) return;
    if (!mismosIds(sedesParaClientes, clientesAsignados)) return;

    const sedeIds = new Set(sedesDisponibles.map((s) => s._id));
    const gruposValidos = gruposDisponibles.filter((g) =>
      (g.sedes || []).some((s) => sedeIds.has(s._id)),
    );
    const grupoIds = new Set(gruposValidos.map((g) => g._id));

    setSedesYGruposAsignados((prev) =>
      prev.filter((id) => sedeIds.has(id) || grupoIds.has(id)),
    );
  }, [
    sedesDisponibles,
    gruposDisponibles,
    gruposListos,
    sedesParaClientes,
    clientesAsignados,
  ]);

  /* =========================
     CARGAR SCOPE (EDITAR)
     SOLO SI NO ES ADMIN
  ========================= */
  useEffect(() => {
    if (!user?._id) return;
    if (esAdminDelUsuarioEditado === null) return;
    if (esAdminDelUsuarioEditado === true) return;

    const fetchScope = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/users/${user._id}/scope`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setClientesAsignados(data?.clientes || []);
        setAlmacenesAsignados(data?.almacenes || []);
        setSedesYGruposAsignados([
          ...(data?.sedes || []),
          ...(data?.sedeGrupos || []),
        ]);
        setAccesoTotalSedes(!!data?.accesoTotalSedes);
      } catch (error) {
        toast.error("Error cargando scope:", error);
      }
    };

    fetchScope();
  }, [user, token, esAdminDelUsuarioEditado]);

  /* =========================
     HANDLERS
  ========================= */
  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await onSave(formData);

      const userId = res?.user?._id || res?._id;

      if (esAdminDelUsuarioEditado === false && userId) {
        const sedeIds = new Set(sedesDisponibles.map((s) => s._id));
        const grupoIds = new Set(gruposVisibles.map((g) => g._id));

        await axios.put(
          `${API_URL}/users/${userId}/scope`,
          {
            clientes: clientesAsignados,
            almacenes: almacenesAsignados,
            sedes: sedesYGruposAsignados.filter((id) => sedeIds.has(id)),
            sedeGrupos: sedesYGruposAsignados.filter((id) => grupoIds.has(id)),
            accesoTotalSedes,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }

      onClose();
    } catch (error) {
      toast.error("Error guardando usuario/scope:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /* =========================
     UI
  ========================= */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl">
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <InputField
                label="Nombre *"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />

              <InputField
                label="Correo *"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />

              <InputField
                label="Contraseña"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required={!user}
              />

              <SelectField
                label="Rol *"
                name="role"
                value={formData.role}
                options={roles}
                valueKey="_id"
                displayFn={(r) => r.name}
                onChange={handleChange}
              />

              {/* =========================
                 ASIGNACIONES (NO ADMIN)
              ========================= */}
              {esAdminDelUsuarioEditado === false && (
                <>
                  <SelectField
                    label="Clientes"
                    isMulti
                    options={clientes}
                    value={clientesAsignados}
                    valueKey="_id"
                    displayFn={(c) => c.name}
                    onChange={(e) =>
                      setClientesAsignados(e.target.value.map((c) => c._id))
                    }
                  />

                  <div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer mb-1">
                      <input
                        type="checkbox"
                        checked={accesoTotalSedes}
                        onChange={(e) => setAccesoTotalSedes(e.target.checked)}
                      />
                      Acceso a todas las sedes
                    </label>

                    {!accesoTotalSedes && (
                      <SelectField
                        label="Sedes y Grupo de sedes"
                        isMulti
                        options={opcionesSedesYGrupos}
                        value={sedesYGruposAsignados}
                        valueKey="_id"
                        displayFn={(o) =>
                          o.__tipo === "grupo" ? `📁 ${o.name}` : o.name
                        }
                        placeholder={
                          clientesAsignados.length
                            ? "Seleccionar..."
                            : "Primero elija al menos un cliente"
                        }
                        isDisabled={!clientesAsignados.length}
                        onChange={(e) =>
                          setSedesYGruposAsignados(e.target.value.map((o) => o._id))
                        }
                      />
                    )}
                  </div>

                  <SelectField
                    label="Bodegas"
                    isMulti
                    options={almacenes}
                    value={almacenesAsignados}
                    valueKey="_id"
                    displayFn={(a) => a.nombre}
                    onChange={(e) =>
                      setAlmacenesAsignados(e.target.value.map((a) => a._id))
                    }
                  />
                </>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" type="button" onClick={onClose}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? "Guardando..." : user ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserForm;
